import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { secopsApi } from '../../lib/secops-api';

interface Investigation {
  id: string;
  name: string;
  description?: string;
  severity: string;
  status: string;
  assigned_to?: string;
  linked_incident_id?: string;
  findings?: string;
  recommendations?: string;
  devices_affected?: string[];
  users_affected?: string[];
  iocs_discovered?: string[];
  created_at: string;
  updated_at: string;
  closed_at?: string;
}

interface TimelineEntry {
  id: string;
  entry_type: string;
  title: string;
  description?: string;
  data?: Record<string, unknown>;
  query_reference?: Record<string, unknown>;
  created_by: string;
  created_at: string;
}

interface ForensicResult {
  events?: Record<string, unknown>[];
  processes?: Record<string, unknown>[];
  hits?: Record<string, unknown>[];
  total?: number;
  execution_time_ms?: number;
  error?: string;
}

interface Report {
  investigation: Record<string, unknown>;
  timeline: TimelineEntry[];
  findings: string;
  iocs_discovered: Array<{ title: string; description?: string; discovered_at: string }>;
  actions_taken: Array<{ title: string; description?: string; taken_at: string }>;
  evidence_collected: number;
  recommendations: string;
  generated_at: string;
}

const STATUS_COLORS: Record<string, string> = {
  open: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-yellow-100 text-yellow-700',
  findings_ready: 'bg-purple-100 text-purple-700',
  escalated: 'bg-red-100 text-red-700',
  closed: 'bg-gray-100 text-gray-600',
};

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-green-100 text-green-700',
  info: 'bg-gray-100 text-gray-600',
};

const ENTRY_TYPE_LABELS: Record<string, string> = {
  evidence: 'Evidence',
  note: 'Note',
  screenshot: 'Screenshot',
  ioc: 'IOC Found',
  action_taken: 'Action Taken',
};

const ENTRY_TYPE_COLORS: Record<string, string> = {
  evidence: 'border-blue-400 bg-blue-50',
  note: 'border-gray-400 bg-gray-50',
  screenshot: 'border-purple-400 bg-purple-50',
  ioc: 'border-red-400 bg-red-50',
  action_taken: 'border-green-400 bg-green-50',
};

type QueryType = 'device-timeline' | 'process-tree' | 'network-activity' | 'file-activity' | 'user-activity' | 'search-ioc';

export default function InvestigationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [investigation, setInvestigation] = useState<Investigation | null>(null);
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'timeline' | 'forensics' | 'report'>('timeline');

  // Timeline form
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [entryForm, setEntryForm] = useState({ entry_type: 'note', title: '', description: '' });

  // Forensics query form
  const [queryType, setQueryType] = useState<QueryType>('device-timeline');
  const [queryForm, setQueryForm] = useState({
    device_id: '', user_id: '', start_time: '', end_time: '',
    root_process_id: '', remote_ip: '', remote_hostname: '',
    file_path: '', operation_type: '',
    ioc_type: 'ip', ioc_value: '', time_range_days: '30',
  });
  const [queryResults, setQueryResults] = useState<ForensicResult | null>(null);
  const [querying, setQuerying] = useState(false);

  // Report
  const [report, setReport] = useState<Report | null>(null);
  const [reportLoading, setReportLoading] = useState(false);

  // Status update
  const [updating, setUpdating] = useState(false);

  const loadInvestigation = useCallback(async () => {
    if (!id) return;
    try {
      const res = await secopsApi.get<{ data: Investigation }>(`/secops/investigations/${id}`);
      setInvestigation(res.data);
    } catch {
      navigate('/secops/forensics');
    }
  }, [id, navigate]);

  const loadTimeline = useCallback(async () => {
    if (!id) return;
    try {
      const res = await secopsApi.get<{ data: TimelineEntry[] }>(`/secops/investigations/${id}?action=timeline`);
      setTimeline(res.data);
    } catch { /* ignore */ }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadInvestigation(), loadTimeline()]).finally(() => setLoading(false));
  }, [loadInvestigation, loadTimeline]);

  async function updateStatus(newStatus: string) {
    if (!id || updating) return;
    setUpdating(true);
    try {
      const res = await secopsApi.put<{ data: Investigation }>(`/secops/investigations/${id}`, { status: newStatus });
      setInvestigation(res.data);
    } catch { /* ignore */ }
    setUpdating(false);
  }

  async function addTimelineEntry(e: React.FormEvent) {
    e.preventDefault();
    if (!id || !entryForm.title.trim()) return;
    try {
      await secopsApi.post(`/secops/investigations/${id}?action=add_timeline`, {
        entry_type: entryForm.entry_type,
        title: entryForm.title,
        description: entryForm.description || undefined,
      });
      setShowAddEntry(false);
      setEntryForm({ entry_type: 'note', title: '', description: '' });
      loadTimeline();
    } catch { /* ignore */ }
  }

  async function deleteTimelineEntry(entryId: string) {
    if (!id) return;
    try {
      await secopsApi.post(`/secops/investigations/${id}?action=delete_timeline`, { entry_id: entryId });
      loadTimeline();
    } catch { /* ignore */ }
  }

  async function runForensicQuery() {
    if (querying) return;
    setQuerying(true);
    setQueryResults(null);
    try {
      let body: Record<string, unknown> = {};
      const path = `/secops/forensics/${queryType}`;

      switch (queryType) {
        case 'device-timeline':
          body = { device_id: queryForm.device_id, start_time: queryForm.start_time, end_time: queryForm.end_time };
          break;
        case 'process-tree':
          body = { device_id: queryForm.device_id, start_time: queryForm.start_time, end_time: queryForm.end_time, root_process_id: queryForm.root_process_id || undefined };
          break;
        case 'network-activity':
          body = { device_id: queryForm.device_id || undefined, user_id: queryForm.user_id || undefined, start_time: queryForm.start_time, end_time: queryForm.end_time, remote_ip: queryForm.remote_ip || undefined, remote_hostname: queryForm.remote_hostname || undefined };
          break;
        case 'file-activity':
          body = { device_id: queryForm.device_id, start_time: queryForm.start_time, end_time: queryForm.end_time, file_path: queryForm.file_path || undefined, operation_type: queryForm.operation_type || undefined };
          break;
        case 'user-activity':
          body = { user_id: queryForm.user_id, start_time: queryForm.start_time, end_time: queryForm.end_time };
          break;
        case 'search-ioc':
          body = { ioc_type: queryForm.ioc_type, value: queryForm.ioc_value, time_range_days: parseInt(queryForm.time_range_days) };
          break;
      }

      const res = await secopsApi.post<{ data: ForensicResult }>(path, body);
      setQueryResults(res.data);
    } catch (err) {
      setQueryResults({ error: err instanceof Error ? err.message : 'Query failed' });
    }
    setQuerying(false);
  }

  async function bookmarkResult(resultSummary: string) {
    if (!id) return;
    try {
      await secopsApi.post(`/secops/investigations/${id}?action=add_timeline`, {
        entry_type: 'evidence',
        title: `Forensic Query: ${queryType}`,
        description: resultSummary,
        query_reference: { query_type: queryType, ...queryForm },
        data: { result_count: queryResults?.total ?? 0 },
      });
      loadTimeline();
    } catch { /* ignore */ }
  }

  async function loadReport() {
    if (!id) return;
    setReportLoading(true);
    try {
      const res = await secopsApi.get<{ data: Report }>(`/secops/investigations/${id}?action=report`);
      setReport(res.data);
    } catch { /* ignore */ }
    setReportLoading(false);
  }

  useEffect(() => {
    if (activeTab === 'report' && !report) loadReport();
  }, [activeTab]);

  if (loading || !investigation) {
    return <div className="flex items-center justify-center h-64 text-gray-400">Loading...</div>;
  }

  const statusTransitions: Record<string, string[]> = {
    open: ['in_progress', 'escalated', 'closed'],
    in_progress: ['findings_ready', 'escalated', 'closed'],
    findings_ready: ['in_progress', 'closed', 'escalated'],
    escalated: ['in_progress', 'closed'],
    closed: ['open'],
  };

  const resultEvents = queryResults?.events ?? queryResults?.processes ?? queryResults?.hits ?? [];

  return (
    <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <button onClick={() => navigate('/secops/forensics')} className="text-sm text-indigo-600 hover:text-indigo-500 mb-2 inline-block">&larr; Back to Investigations</button>
          <h1 className="text-2xl font-bold text-gray-900">{investigation.name}</h1>
          {investigation.description && <p className="text-sm text-gray-500 mt-1">{investigation.description}</p>}
          <div className="flex items-center gap-3 mt-2">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[investigation.status] ?? 'bg-gray-100'}`}>
              {investigation.status.replace(/_/g, ' ')}
            </span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${SEVERITY_COLORS[investigation.severity] ?? 'bg-gray-100'}`}>
              {investigation.severity}
            </span>
            {investigation.assigned_to && <span className="text-xs text-gray-500">Assigned: {investigation.assigned_to}</span>}
            {investigation.linked_incident_id && <span className="text-xs text-gray-500">Incident: {investigation.linked_incident_id}</span>}
          </div>
        </div>
        <div className="flex gap-2">
          {(statusTransitions[investigation.status] ?? []).map((s) => (
            <button key={s} onClick={() => updateStatus(s)} disabled={updating}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              {s === 'in_progress' ? 'Start' : s === 'findings_ready' ? 'Mark Findings Ready' : s === 'escalated' ? 'Escalate' : s === 'closed' ? 'Close' : 'Reopen'}
            </button>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6">
          {(['timeline', 'forensics', 'report'] as const).map((tab) => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${activeTab === tab ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {tab === 'timeline' ? 'Timeline' : tab === 'forensics' ? 'Forensics' : 'Report'}
            </button>
          ))}
        </nav>
      </div>

      {/* Timeline Tab */}
      {activeTab === 'timeline' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">Investigation Timeline</h2>
            <button onClick={() => setShowAddEntry(true)} className="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-500">Add Entry</button>
          </div>

          {timeline.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-400">No timeline entries yet. Add notes, evidence, or IOC findings.</div>
          ) : (
            <div className="space-y-3">
              {timeline.map((entry) => (
                <div key={entry.id} className={`bg-white rounded-lg shadow border-l-4 p-4 ${ENTRY_TYPE_COLORS[entry.entry_type] ?? 'border-gray-400 bg-gray-50'}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-gray-500 uppercase">{ENTRY_TYPE_LABELS[entry.entry_type] ?? entry.entry_type}</span>
                        <span className="text-xs text-gray-400">{new Date(entry.created_at).toLocaleString()}</span>
                      </div>
                      <h3 className="font-medium text-gray-900 mt-1">{entry.title}</h3>
                      {entry.description && <p className="text-sm text-gray-600 mt-1">{entry.description}</p>}
                      {entry.query_reference && (
                        <div className="mt-2 text-xs text-gray-500 bg-gray-100 rounded px-2 py-1 inline-block">
                          Query: {(entry.query_reference as Record<string, unknown>).query_type as string}
                        </div>
                      )}
                    </div>
                    <button onClick={() => deleteTimelineEntry(entry.id)} className="text-gray-400 hover:text-red-500 text-xs">Remove</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add Entry Modal */}
          {showAddEntry && (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowAddEntry(false)}>
              <form onClick={(e) => e.stopPropagation()} onSubmit={addTimelineEntry} className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 space-y-4">
                <h2 className="text-lg font-semibold text-gray-900">Add Timeline Entry</h2>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select value={entryForm.entry_type} onChange={(e) => setEntryForm({ ...entryForm, entry_type: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="note">Note</option>
                    <option value="evidence">Evidence</option>
                    <option value="ioc">IOC Found</option>
                    <option value="action_taken">Action Taken</option>
                    <option value="screenshot">Screenshot</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                  <input type="text" value={entryForm.title} onChange={(e) => setEntryForm({ ...entryForm, title: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea value={entryForm.description} onChange={(e) => setEntryForm({ ...entryForm, description: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" rows={3} />
                </div>
                <div className="flex justify-end gap-3">
                  <button type="button" onClick={() => setShowAddEntry(false)} className="px-4 py-2 text-sm text-gray-600">Cancel</button>
                  <button type="submit" className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-500">Add Entry</button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}

      {/* Forensics Tab */}
      {activeTab === 'forensics' && (
        <div className="space-y-4">
          {/* Query Type Selector */}
          <div className="flex flex-wrap gap-2">
            {([
              ['device-timeline', 'Device Timeline'],
              ['process-tree', 'Process Tree'],
              ['network-activity', 'Network Activity'],
              ['file-activity', 'File Activity'],
              ['user-activity', 'User Activity'],
              ['search-ioc', 'IOC Search'],
            ] as [QueryType, string][]).map(([type, label]) => (
              <button key={type} onClick={() => { setQueryType(type); setQueryResults(null); }}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${queryType === type ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                {label}
              </button>
            ))}
          </div>

          {/* Query Form */}
          <div className="bg-white rounded-lg shadow p-4 space-y-3">
            {queryType !== 'search-ioc' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Start Time</label>
                  <input type="datetime-local" value={queryForm.start_time} onChange={(e) => setQueryForm({ ...queryForm, start_time: new Date(e.target.value).toISOString() })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">End Time</label>
                  <input type="datetime-local" value={queryForm.end_time} onChange={(e) => setQueryForm({ ...queryForm, end_time: new Date(e.target.value).toISOString() })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>
            )}

            {(queryType === 'device-timeline' || queryType === 'process-tree' || queryType === 'file-activity') && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Device ID *</label>
                <input type="text" value={queryForm.device_id} onChange={(e) => setQueryForm({ ...queryForm, device_id: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="device-uuid" />
              </div>
            )}

            {queryType === 'network-activity' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Device ID</label>
                  <input type="text" value={queryForm.device_id} onChange={(e) => setQueryForm({ ...queryForm, device_id: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Remote IP</label>
                  <input type="text" value={queryForm.remote_ip} onChange={(e) => setQueryForm({ ...queryForm, remote_ip: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>
            )}

            {queryType === 'user-activity' && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">User ID / UPN *</label>
                <input type="text" value={queryForm.user_id} onChange={(e) => setQueryForm({ ...queryForm, user_id: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="user@company.com" />
              </div>
            )}

            {queryType === 'search-ioc' && (
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">IOC Type</label>
                  <select value={queryForm.ioc_type} onChange={(e) => setQueryForm({ ...queryForm, ioc_type: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="ip">IP Address</option>
                    <option value="domain">Domain</option>
                    <option value="hash">File Hash</option>
                    <option value="filename">Filename</option>
                    <option value="registry_key">Registry Key</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Value *</label>
                  <input type="text" value={queryForm.ioc_value} onChange={(e) => setQueryForm({ ...queryForm, ioc_value: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Days Back</label>
                  <input type="number" value={queryForm.time_range_days} onChange={(e) => setQueryForm({ ...queryForm, time_range_days: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" min="1" max="90" />
                </div>
              </div>
            )}

            <div className="flex items-center gap-3">
              <button onClick={runForensicQuery} disabled={querying} className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-500 disabled:opacity-50">
                {querying ? 'Querying ADX...' : 'Run Query'}
              </button>
              {queryResults && !queryResults.error && (
                <button onClick={() => bookmarkResult(`${queryType}: ${resultEvents.length} events found`)} className="px-3 py-1.5 border border-indigo-300 text-indigo-600 text-sm rounded-lg hover:bg-indigo-50">
                  Add to Timeline
                </button>
              )}
            </div>
          </div>

          {/* Query Results */}
          {queryResults && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              {queryResults.error ? (
                <div className="p-6 text-center text-red-500">{queryResults.error}</div>
              ) : resultEvents.length === 0 ? (
                <div className="p-6 text-center text-gray-400">No events found</div>
              ) : (
                <>
                  <div className="px-4 py-3 bg-gray-50 border-b text-sm text-gray-600">
                    {queryResults.total} events found in {queryResults.execution_time_ms}ms
                  </div>
                  <div className="overflow-x-auto max-h-96 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          {Object.keys(resultEvents[0]).map((col) => (
                            <th key={col} className="px-3 py-2 text-left font-medium text-gray-500 whitespace-nowrap">{col}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {resultEvents.slice(0, 200).map((row, i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            {Object.values(row).map((val, j) => (
                              <td key={j} className="px-3 py-1.5 text-gray-700 whitespace-nowrap max-w-xs truncate">{String(val ?? '')}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {resultEvents.length > 200 && (
                    <div className="px-4 py-2 bg-gray-50 border-t text-xs text-gray-500">Showing 200 of {resultEvents.length} events</div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Report Tab */}
      {activeTab === 'report' && (
        <div className="space-y-4">
          {reportLoading ? (
            <div className="bg-white rounded-lg shadow p-12 text-center text-gray-400">Generating report...</div>
          ) : !report ? (
            <div className="bg-white rounded-lg shadow p-12 text-center text-gray-400">Failed to generate report</div>
          ) : (
            <>
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900">Investigation Report</h2>
                <button onClick={loadReport} className="px-3 py-1.5 border border-gray-300 text-sm rounded-lg hover:bg-gray-50">Refresh</button>
              </div>

              <div className="bg-white rounded-lg shadow p-6 space-y-6">
                {/* Summary */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 uppercase mb-2">Summary</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div><span className="text-gray-500">Status:</span> <span className="font-medium">{investigation.status}</span></div>
                    <div><span className="text-gray-500">Severity:</span> <span className="font-medium">{investigation.severity}</span></div>
                    <div><span className="text-gray-500">Duration:</span> <span className="font-medium">{String((report.investigation as Record<string, unknown>).duration_hours)}h</span></div>
                    <div><span className="text-gray-500">Evidence:</span> <span className="font-medium">{report.evidence_collected} items</span></div>
                  </div>
                </div>

                {/* Timeline */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 uppercase mb-2">Timeline ({report.timeline.length} entries)</h3>
                  <div className="space-y-2">
                    {report.timeline.map((entry) => (
                      <div key={entry.id} className="flex gap-3 text-sm">
                        <span className="text-gray-400 whitespace-nowrap">{new Date(entry.created_at).toLocaleString()}</span>
                        <span className="text-xs font-semibold text-gray-500 uppercase">[{entry.entry_type}]</span>
                        <span className="text-gray-700">{entry.title}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Findings */}
                {report.findings && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 uppercase mb-2">Findings</h3>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{report.findings}</p>
                  </div>
                )}

                {/* IOCs */}
                {report.iocs_discovered.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 uppercase mb-2">IOCs Discovered ({report.iocs_discovered.length})</h3>
                    <div className="space-y-2">
                      {report.iocs_discovered.map((ioc, i) => (
                        <div key={i} className="text-sm"><span className="font-medium text-red-700">{ioc.title}</span>{ioc.description && <span className="text-gray-500"> — {ioc.description}</span>}</div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions Taken */}
                {report.actions_taken.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 uppercase mb-2">Actions Taken ({report.actions_taken.length})</h3>
                    <div className="space-y-2">
                      {report.actions_taken.map((action, i) => (
                        <div key={i} className="text-sm"><span className="font-medium text-green-700">{action.title}</span>{action.description && <span className="text-gray-500"> — {action.description}</span>}</div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recommendations */}
                {report.recommendations && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 uppercase mb-2">Recommendations</h3>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{report.recommendations}</p>
                  </div>
                )}

                <div className="text-xs text-gray-400 pt-4 border-t">Report generated: {new Date(report.generated_at).toLocaleString()}</div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
